import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useStyles, useTheme } from 'uinix-ui';

import { useEscape } from '../../hooks/useEscape';
import { widths } from '../../tokens/sizes/widths';
import { zIndices } from '../../tokens/zIndices';
import { SharedPanelProps } from '../../types/SharedPanelProps';
import { UserAction } from '../../types/UserAction';
import { Backdrop } from '../Backdrop/Backdrop';
import { Box } from '../Box/Box';
import { Button } from '../Button/Button';
import { ContentContainer } from '../ContentContainer/ContentContainer';
import { Footer } from '../Footer/Footer';
import { Header } from '../Header/Header';
import { Layout } from '../Layout/Layout';
import { usePageSidebarContext } from '../PageSidebar/PageSidebarContext';
import { SearchInput } from '../SearchInput/SearchInput';
import { StatusMessage } from '../StatusMessage/StatusMessage';
import { FLOATING_FULL_WIDTH } from './constants';
import { basePanelWrapperStyle, getPanelPosition } from './utils';

export const Panel = memo(
  ({
    actions = [],
    ariaLabel,
    cancelAction,
    animate,
    backAction,
    children,
    chips,
    enableBackdrop = false,
    enableContentPadding = true,
    enableMaximize = false,
    maximizeActionPlacement = 'leading',
    expandWidth,
    onWidthChange,
    footer: footerProps,
    hidden,
    icon,
    loadingContent,
    mode,
    moreActions,
    moreActionsPosition,
    offsetTop,
    placeholderContent,
    position,
    hideRailGutter,
    rightContent,
    sideContent,
    statusMessage,
    search,
    title,
    subtitle,
    width: initialWidth = '100%',
    welcomeContent,
    borderRadius,
    disableBorderOverlap = false,
    disableTopBorder = false,
    zIndex = zIndices.panel,
  }: SharedPanelProps) => {
    const styles = useStyles();
    const theme = useTheme();
    const intl = useIntl();

    const isSideContentVisible = sideContent?.isVisible ?? false;
    const isRightContentVisible = rightContent?.isVisible ?? false;

    const toggleAction = sideContent
      ? {
          ...sideContent.action,
        }
      : undefined;

    const [width, setWidth] = useState(initialWidth);
    const [searchValue, setSearchValue] = useState('');

    useEffect(() => {
      setWidth(initialWidth);
    }, [initialWidth]);

    const pageSidebarContext = usePageSidebarContext();
    const reportWidth = onWidthChange ?? pageSidebarContext?.setEffectiveWidth;

    // When the host wires up `onToggleMaximize`, maximize is controlled and
    // shared by the host (see PageSidebarContext); otherwise the panel owns it
    // via local `width` state. Reading the shared flag here keeps every sibling
    // panel's enlarged state in lockstep.
    const onToggleMaximizeFromHost = pageSidebarContext?.onToggleMaximize;
    const isMaximizeControlled = onToggleMaximizeFromHost != null;
    const isMaximized = isMaximizeControlled
      ? Boolean(pageSidebarContext?.isMaximized)
      : width === '100%';

    const toggleMaximize = useCallback(() => {
      if (onToggleMaximizeFromHost) {
        onToggleMaximizeFromHost();
      } else {
        setWidth((current) => (current === '100%' ? initialWidth : '100%'));
      }
    }, [onToggleMaximizeFromHost, initialWidth]);

    useLayoutEffect(() => {
      if (reportWidth) {
        const effectiveWidth =
          isMaximized && expandWidth !== undefined ? expandWidth : initialWidth;
        reportWidth(effectiveWidth as string | number | undefined);
      }
    }, [reportWidth, isMaximized, initialWidth, expandWidth]);

    const handleHide = useCallback(() => hidden?.onHide?.(), [hidden]);

    useEscape(handleHide, Boolean(hidden && hidden.onHide && !hidden.isHidden));

    const effectiveMode = pageSidebarContext?.modeOverride ?? mode;

    const isLoading = loadingContent?.isLoading;
    const isFloatingMode =
      effectiveMode === 'floating' ||
      effectiveMode === 'floating-full-width' ||
      effectiveMode === 'floating-sidepanel';

    const isInsidePageSidebar = Boolean(pageSidebarContext);
    const shouldShowCloseButton =
      !isInsidePageSidebar &&
      (effectiveMode === 'fullscreen' || isFloatingMode || cancelAction);

    const maximizeAction = useMemo((): UserAction | undefined => {
      if (
        !enableMaximize ||
        initialWidth === '100%' ||
        effectiveMode === 'fullscreen' ||
        effectiveMode === 'floating-full-width'
      ) {
        return undefined;
      }
      return {
        text: isMaximized ? 'Minimize' : 'Maximize',
        icon: isMaximized ? 'minimize' : 'maximize',
        size: 's' as const,
        mode: 'icon',
        tooltip: isMaximized
          ? intl.formatMessage({
              id: 'Panel.minimizeButton',
              defaultMessage: 'Minimize',
              description: 'Button label for minimizing the panel',
            })
          : intl.formatMessage({
              id: 'Panel.maximizeButton',
              defaultMessage: 'Maximize',
              description: 'Button label for maximizing the panel',
            }),
        onClick: toggleMaximize,
      };
    }, [
      enableMaximize,
      initialWidth,
      isMaximized,
      effectiveMode,
      intl,
      toggleMaximize,
    ]);

    const headerActions = useMemo(() => {
      const _headerActions: UserAction[] = actions.map((action) => ({
        disabled: isLoading || action.disabled,
        mode: 'icon' as const,
        ...action,
      }));

      // With trailing placement the maximize button joins the right-side actions
      // (leftmost of the cluster); leading placement renders it left of the title.
      if (maximizeActionPlacement === 'trailing' && maximizeAction) {
        _headerActions.unshift({
          ...maximizeAction,
          disabled: isLoading || maximizeAction.disabled,
        });
      }

      if (shouldShowCloseButton) {
        _headerActions.push({
          icon: 'x',
          text: intl.formatMessage({
            id: 'Panel.closeButton',
            defaultMessage: 'Close',
            description: 'Button label for closing the panel',
          }),
          mode: 'icon',
          onClick: handleHide,
          ...cancelAction,
        });
      }

      return _headerActions;
    }, [
      actions,
      cancelAction,
      intl,
      isLoading,
      maximizeAction,
      maximizeActionPlacement,
      handleHide,
      shouldShowCloseButton,
    ]);

    const hiddenAction = useMemo(() => {
      if (!hidden || shouldShowCloseButton || isInsidePageSidebar) {
        return null;
      }
      const { isHidden = false, placement, onHide, onShow } = hidden;
      if (!isHidden && onHide) {
        return (
          <Button
            mode="area"
            icon={placement === 'right' ? 'collapse-left' : 'collapse-right'}
            label="Hide"
            tooltip={intl.formatMessage({
              id: 'Panel.hideTooltip',
              defaultMessage: 'Hide',
              description: 'Tooltip for hiding the panel',
            })}
            onClick={handleHide}
          />
        );
      } else if (isHidden && onShow) {
        return (
          <Button
            mode="area"
            icon={placement === 'right' ? 'collapse-right' : 'collapse-left'}
            label="Show"
            tooltip={intl.formatMessage({
              id: 'Panel.showTooltip',
              defaultMessage: 'Show',
              description: 'Tooltip for showing the panel',
            })}
            onClick={onShow}
          />
        );
      } else {
        return null;
      }
    }, [hidden, shouldShowCloseButton, isInsidePageSidebar, intl, handleHide]);

    const enableHiddenContent = hidden?.isHidden && hidden?.content;
    const enablePosition =
      Boolean(position) || isFloatingMode || effectiveMode === 'fullscreen';

    const header =
      !enableHiddenContent && title ? (
        <Header
          actions={headerActions}
          backAction={backAction}
          leadingAction={
            maximizeActionPlacement === 'trailing' ? undefined : maximizeAction
          }
          toggleAction={toggleAction}
          chips={chips}
          icon={icon}
          iconPosition={icon?.position}
          moreActions={moreActions}
          moreActionsPosition={moreActionsPosition}
          title={title}
          subtitle={subtitle}
        />
      ) : undefined;

    const _footer = footerProps && <Footer {...footerProps} />;

    const footer =
      _footer && effectiveMode === 'fullscreen' ? (
        <Box
          // @ts-expect-error -- uinix type limitation, valid usage
          styles={[componentStyles.contentContainer]}
          styleProps={{ mode: effectiveMode }}
        >
          {_footer}
        </Box>
      ) : (
        _footer
      );

    const statusMessageElement = statusMessage && (
      <Box styles={[componentStyles.statusMessage]} styleProps={{ footer }}>
        <StatusMessage {...statusMessage} />
      </Box>
    );

    const p = enableContentPadding ? 4 : 0;
    const useExpandWidth = isMaximized && expandWidth !== undefined;
    let w: string | number = 'max-content';
    if (enableHiddenContent) {
      w = 'fit-content';
    } else if (useExpandWidth) {
      w = expandWidth;
    } else if (isMaximized) {
      w = '100%';
    }

    const mainContentWidth = useExpandWidth
      ? expandWidth
      : calculateWidth(initialWidth, effectiveMode, width);

    const mainContentElement = (
      <Layout direction="column" h="100%" minH={0} w={mainContentWidth}>
        {enableHiddenContent ? (
          hidden?.content
        ) : (
          <Box
            h="100%"
            overflowY="auto"
            p={p}
            // @ts-ignore -- uinix type limitation, valid usage
            styles={[componentStyles.contentContainer]}
            styleProps={{ mode: effectiveMode }}
          >
            <ContentContainer
              loadingContent={loadingContent}
              placeholderContent={placeholderContent}
              welcomeContent={welcomeContent}
            >
              <>
                {search && (
                  <SearchInput
                    name="panel-search"
                    placeholder={
                      search.placeholder ??
                      intl.formatMessage({
                        id: 'Panel.searchPlaceholder',
                        defaultMessage: 'Search',
                        description:
                          'Placeholder text for search input in panel',
                      })
                    }
                    value={searchValue}
                    onChange={(text) => {
                      setSearchValue(text ?? '');
                      search.onChange(text ?? '');
                    }}
                  />
                )}
                {children}
              </>
            </ContentContainer>
          </Box>
        )}
        {statusMessageElement}
        {!enableHiddenContent && footer}
        {hiddenAction && (
          <Box
            // @ts-ignore -- uinix type limitation, valid usage
            styles={componentStyles.hiddenAction}
            styleProps={{
              placement: hidden?.placement,
            }}
          >
            {hiddenAction}
          </Box>
        )}
      </Layout>
    );

    const sideContentElement = sideContent && isSideContentVisible && (
      <>
        <Layout
          direction="column"
          // @ts-ignore -- uinix type limitation, valid usage
          styles={componentStyles.sideContent}
        >
          <ContentContainer
            loadingContent={sideContent.loadingContent}
            placeholderContent={sideContent.placeholderContent}
            welcomeContent={sideContent.welcomeContent}
          >
            <>{sideContent.content}</>
          </ContentContainer>
        </Layout>
      </>
    );

    const rightContentElement = rightContent && isRightContentVisible && (
      <>
        <Layout
          direction="column"
          id="aa-right-content"
          // @ts-ignore -- uinix type limitation, valid usage
          styles={componentStyles.rightContent}
          styleProps={{ width: rightContent.width }}
        >
          <ContentContainer welcomeContent={rightContent.welcomeContent}>
            {rightContent.content}
          </ContentContainer>
        </Layout>
      </>
    );

    const constrainMaxWidth = resolveMaxWidth({
      mode: effectiveMode,
      mainContentWidth,
      isSideContentVisible,
      isRightContentVisible,
      rightContentWidth: rightContent?.width ?? 400,
      theme,
    });

    const panelComponent = (
      <Layout
        as="aside"
        direction="column"
        aria-label={
          ariaLabel ?? (typeof title === 'string' ? title : undefined)
        }
        flex="none"
        justify="space-between"
        styles={[
          // Only animate here when not using positioned wrapper (inline mode)
          animate && !enablePosition ? styles.animations.slideInRight : null,
          componentStyles.container,
        ]}
        styleProps={{
          borderRadius,
          disableBorderOverlap,
          disableTopBorder,
          isFloatingMode,
          maxW: constrainMaxWidth,
        }}
        w={mode !== 'fullscreen' ? w : undefined}
      >
        {!enableHiddenContent && header}
        <Layout
          h="100%"
          minH={0}
          styles={[styles.background.image(welcomeContent?.backgroundURL)]}
        >
          {sideContentElement}
          {mainContentElement}
          {rightContentElement}
        </Layout>
      </Layout>
    );

    let panel = enablePosition ? (
      <Box
        // @ts-ignore -- uinix type limitation, valid usage
        styles={[
          basePanelWrapperStyle({
            mode: effectiveMode,
            zIndex,
            expandWidth: useExpandWidth ? expandWidth : undefined,
          }),
          effectiveMode !== 'inline' ? styles.position : undefined,
          effectiveMode === 'fullscreen' ? styles.size.fullscreen : undefined,
          // Animate on outer wrapper so shadow moves with panel
          animate ? styles.animations.slideInRight : null,
        ]}
        styleProps={getPanelPosition({
          mode: effectiveMode,
          offsetTop,
          position,
          hideRailGutter,
        })}
      >
        {panelComponent}
      </Box>
    ) : (
      panelComponent
    );

    panel = enableBackdrop ? <Backdrop>{panel}</Backdrop> : panel;

    return panel;
  },
);

const componentStyles = {
  container: ({
    isFloatingMode,
    borderRadius,
    disableBorderOverlap,
    disableTopBorder,
    maxW,
  }: {
    isFloatingMode?: boolean;
    borderRadius: 'm' | 'l';
    disableBorderOverlap?: boolean;
    disableTopBorder?: boolean;
    maxW: string | number;
  }) => ({
    backgroundColor: 'background',
    border: 'border.divider',
    // A panel is usually embedded in bordered layouts: content-box plus a -2px
    // top margin makes its border overlap the parent's for convenient UI
    // composition. disableBorderOverlap turns it into a plain border-box card
    // so it aligns flush with sibling content when it floats on its own.
    boxSizing: disableBorderOverlap ? 'border-box' : 'content-box',
    height: '100%',
    // When the slot already draws a top border, drop the panel's own top border
    // and the -2px nudge so the two don't stack into a doubled 2px line.
    marginTop: disableBorderOverlap || disableTopBorder ? 0 : '-2px',
    position: 'relative',
    borderRadius: (borderRadius ?? isFloatingMode) ? 'ml' : undefined,
    maxWidth: maxW,
    ...(disableTopBorder ? { borderTopWidth: 0 } : {}),
  }),
  contentContainer: ({ mode }: { mode: SharedPanelProps['mode'] }) =>
    mode === 'fullscreen'
      ? {
          width: 'panel.width.l',
          margin: '0 auto',
        }
      : {},
  sideContent: ({ placement = 'left' }: { placement: 'left' | 'right' }) => {
    const isLeft = placement === 'left';
    return {
      flex: 'none',
      width: 'sideContent.width',
      border: 'border.divider',
      borderLeftColor: !isLeft ? undefined : 'background',
      borderRightColor: !isLeft ? 'background' : undefined,
      borderBottomWidth: 0,
      borderTopWidth: 0,
    };
  },
  rightContent: ({ width }: { width?: number }) => ({
    flex: 'none',
    width: width ?? 400,
    height: '100%',
    // Bounds the slot within the flex row so embedded content resolves against
    // a bounded height and can scroll inside it. The slot itself must not
    // scroll: an `overflow-y` here makes CSS coerce `overflow-x` to `auto` too,
    // and an embedded panel's `hiddenAction` deliberately sits past the slot's
    // left edge (it overlays the panel to its left), so it would be clipped.
    minHeight: 0,
    border: 'border.divider',
    borderLeftColor: undefined,
    borderRightColor: 'background',
    borderBottomWidth: 0,
    borderTopWidth: 0,
  }),
  hiddenAction: ({ placement = 'left' }: { placement?: 'left' | 'right' }) => {
    const isLeft = placement === 'left';
    return {
      backgroundColor: 'background',
      border: 'border.divider',
      borderLeftColor: isLeft ? undefined : 'background',
      borderRightColor: isLeft ? 'background' : undefined,
      borderBottomLeftRadius: isLeft ? 'm' : undefined,
      borderBottomRightRadius: isLeft ? undefined : 'm',
      borderTopLeftRadius: isLeft ? 'm' : undefined,
      borderTopRightRadius: isLeft ? undefined : 'm',
      left: isLeft ? 0 : undefined,
      marginTop: '-1px', // offset border
      position: 'absolute',
      right: isLeft ? undefined : 0,
      top: 12,
      transform: `translateX(${isLeft ? '-' : ''}100%)`,
      width: '36px',
      '& button': {
        alignItems: 'center',
        justifyContent: 'center',
      },
    };
  },
  statusMessage: ({ footer }: { footer?: boolean }) => ({
    position: 'absolute',
    bottom: footer ? '76px' : 3, // 76px = footer.height (64px) + 3 (12px)
    left: 4,
    right: 4,
  }),
};

const calculateWidth = (
  initialWidth: SharedPanelProps['width'],
  mode: SharedPanelProps['mode'],
  width: SharedPanelProps['width'],
) => {
  if (initialWidth === 'm' || initialWidth === 'l') {
    //If enableMaximize is enabled and width is 100%, we need to return 100%
    if (width === '100%') {
      return '100%';
    }

    //Default for width token m or l
    return `panel.width.${initialWidth}`;
  }

  if (mode === 'floating-full-width') {
    return FLOATING_FULL_WIDTH;
  }

  return width;
};

const resolveMaxWidth = ({
  mode,
  mainContentWidth,
  isSideContentVisible,
  isRightContentVisible,
  rightContentWidth,
  theme,
}: {
  mode: SharedPanelProps['mode'];
  mainContentWidth: string | number | undefined;
  isSideContentVisible: boolean;
  isRightContentVisible: boolean;
  rightContentWidth: number;
  theme: { sizes: Record<string, unknown> };
}): string | number | undefined => {
  if (mode === 'fullscreen' || mode === 'floating-full-width') {
    return FLOATING_FULL_WIDTH;
  }
  if (mainContentWidth === '100%' || mainContentWidth === undefined) {
    return undefined;
  }

  const sizes = theme.sizes as Record<string, number>;
  let mainPx: number;
  if (
    mainContentWidth === 'panel.width.m' ||
    mainContentWidth === 'panel.width.l'
  ) {
    mainPx = sizes[mainContentWidth] ?? 0;
  } else if (typeof mainContentWidth === 'number') {
    mainPx = mainContentWidth;
  } else if (
    typeof mainContentWidth === 'string' &&
    mainContentWidth.endsWith('px')
  ) {
    mainPx = parseInt(mainContentWidth, 10);
  } else {
    return mainContentWidth;
  }

  let total = mainPx;
  if (isSideContentVisible) {
    total += sizes['sideContent.width'] ?? widths['sideContent.width'];
  }
  if (isRightContentVisible) {
    total += rightContentWidth;
  }
  return total;
};
